/**
 * Road world — chunked, recycled, cel-shaded (Stage 4e city).
 *
 * Stage 1a + Stage 4e. A THREE.Group of `chunksAhead + chunksBehind` reusable
 * road chunks. Each chunk is `chunkLength` m long and spans the full corridor
 * (lanes + shoulders): flat grey asphalt plane (NO texture — see below), dashed
 * white lane markings for `laneCount` lanes, solid shoulder edge lines,
 * orange guardrail boxes on both sides at `guardrailHeight`. Chunks are
 * positioned along Z so the road streams toward +Z (past the camera) as
 * `player.distance` grows: the player stays at z=0 facing -Z, and each chunk
 * carries a road-distance anchor `anchorD` whose near edge renders at
 * z = distance - anchorD (see render/roadScroll.ts). Chunks that pass behind
 * the player are recycled to the front — no allocation after warmup.
 *
 * Also owns the sky dome (sky.png on a large inside-facing cylinder — seam-
 * free at the horizon in portrait 9:16) + hemisphere/directional light
 * setup. The sun sits behind-right of the player (+x, +y, +z quadrant) per
 * the design spec, so the rider's shadow falls forward-left. Its final
 * placement + shadow frustum are owned by render/riderCaster.ts, which
 * re-parents it to the camera rig root; road chunks receiveShadow.
 *
 * Textures load async via `load()` (sky only now); the biome system owns the
 * per-biome screen-space sky background.
 *
 * Art direction (DESIGN_SYSTEM v1.1 — "toy plastic")
 * --------------------------------------------------
 * - The asphalt TEXTURE is gone. A tiled photographic asphalt map fought the
 *   stylized world and muddied the lane markings; the road is a flat mid-grey
 *   (ASPHALT_COLOR) that is clearly lighter than tyre-black, so the white
 *   markings pop and the vehicles read against it. As of the 2026-08-01
 *   playtest it carries a PROCEDURAL (TSL, still image-free) detail node over
 *   that grey — a large soft mottle, a near-camera speckle and a wheel-track
 *   band per lane, together inside a ~6% luminance budget. See plastic.ts
 *   `asphaltMaterial`; `updateWorld` feeds it the travel distance.
 * - The guardrail is a W-BEAM profile with posts every sub-segment (5 m), not
 *   a box: `buildGuardrailGeometry` below.
 * - The road is CLEAR-COATED (MeshPhysicalMaterial via
 *   `plasticMaterial({ kind: "asphalt" })`). It used to be matte Lambert on the
 *   theory that asphalt has no sheen, but next to the toy-plastic props it read
 *   as plain and dead. The `asphalt` params (roughness 0.5 / clearcoat 0.3 /
 *   coatRoughness 0.3) add ONE broad blurred highlight off the environment + sun
 *   while the surface still glances as flat light grey. Guardrails are CANDY
 *   plastic — the same glossy vinyl-toy response as the vehicles. The MARKINGS
 *   stay MeshBasicMaterial: unlit, always crisp white, never dimmed by a shadow
 *   or a biome light tint, and never streaked by a coat.
 * - Lighting is a FOUR-part rig (2026-08-01 lighting pass): an analytic
 *   sky/ground gradient environment that is re-tinted per biome (the
 *   wraparound that makes clearcoat read as toy plastic — render/envGradient.ts),
 *   one warm key sun with soft shadows, a low hemisphere fill, and a shadowless
 *   ground-BOUNCE directional from below the horizon opposite the sun. The
 *   gradient environment replaced three's neutral RoomEnvironment PMREM: the
 *   room was the same grey lightbox in every biome, so nothing in the world
 *   picked up the colour of what was actually around it.
 */

import {
  Group,
  Mesh,
  BufferGeometry,
  Float32BufferAttribute,
  PlaneGeometry,
  MeshBasicMaterial,
  Color,
  HemisphereLight,
  DirectionalLight,
  TextureLoader,
  SRGBColorSpace,
  type Material,
  type Texture,
  type Scene,
  type WebGPURenderer,
} from "three/webgpu";
import { createGradientEnvironment, type GradientEnvironment } from "./envGradient";
import { TUNING } from "../sim/tuning";
import { curveOffset, curveTangent } from "./curve";
import { initialAnchor, recycleAnchor } from "./roadScroll";
import { asphaltMaterial, plasticMaterial, type AsphaltMaterial } from "./plastic";
import { applyEarthCurve } from "./earthCurve";

const R = TUNING.road;

// Reused geometry/material singletons — built once, shared by all chunks.
/**
 * Flat road grey. Deliberately mid-tone: light enough that the white markings
 * and the rider's shadow both read, dark enough to sit under saturated vehicle
 * paint, and clearly lighter than tyre-black (0x0a0a0a).
 *
 * Retuned for v1.1: ACES filmic tone mapping compresses the mid-tones, so the
 * cel build's 0x5a5e66 came out of the pipe as a near-charcoal. The albedo is
 * lifted toward the DESIGN_SYSTEM road hex (#9aa0a8) so the ON-SCREEN result
 * lands back on flat light-grey asphalt.
 */
const ASPHALT_COLOR = 0xa9aeb6;
const LANE_MARK_COLOR = 0xf2f2e8;
const SHOULDER_LINE_COLOR = 0xe8e8d8;
/** Warm orange guardrail — the DESIGN_SYSTEM palette hex, unchanged per biome. */
const GUARDRAIL_COLOR = 0xe8912c;

// --- Light rig numbers (DESIGN_SYSTEM v1.1) --------------------------------
/**
 * `scene.environmentIntensity` — how strongly the gradient environment lights
 * the world. This is the dial that decides how "studio lightbox" the scene
 * reads: at 0 you are back to a flat key+fill, too high and the wraparound
 * fills every cast shadow back in and they read as smudges.
 *
 * 0.42 -> 0.80 with the 2026-08-01 lighting pass. NOT a brightening: the
 * RoomEnvironment PMREM this replaced was a white box with EMISSIVE panels, so
 * its average radiance was far above its albedo, while the gradient's two
 * biome colours average about 0.45 linear. 0.80 is the value that puts total
 * scene luminance back where the room had it — measured, not guessed: mean
 * frame luminance across the four outdoor biomes lands within 3% of the
 * pre-change build (city 162.3 -> 157.2, bridge 149.6 -> 149.5, forest
 * 160.7 -> 155.4, fields 166.9 -> 164.1 on 0-255). The sun/shadow contrast the
 * shadow pass bought is therefore unchanged; only the COLOUR of the ambient
 * moved.
 */
const ENV_INTENSITY = 0.8;
/**
 * Default environment gradient colours, used until the biome system pushes its
 * own (and by the unit tests / the QC viewer, which have no biome system).
 * These are the neutral daylight pair the RoomEnvironment lightbox used to
 * approximate: a cool bright sky above, a warm mid ground below.
 */
const ENV_SKY_DEFAULT = 0xdfeaff;
const ENV_GROUND_DEFAULT = 0x8c8578;
/**
 * The BOUNCE fill: a second directional light aimed UP-and-back from below the
 * horizon, opposite the key sun. It casts no shadow and is deliberately weak —
 * its whole job is to lift the faces the sun and the sky-half of the
 * environment both miss (a car's shaded flank, the underside of a bridge deck,
 * the back of a tree) by roughly a tenth of a stop, which is what a real
 * ground bounce does.
 *
 * BUDGET: this is not extra light on the scene. It is paid for out of the
 * hemisphere fill (HEMI 0.26 -> 0.17) so the total ambient a road-facing
 * surface receives is unchanged and the cast shadows keep the contrast the
 * 2026-08-01 shadow pass bought them. The difference is DIRECTIONALITY: the
 * hemisphere lifts everything that faces up, the bounce lifts what faces the
 * sun's opposite, which is where a shadowed side actually is.
 */
const BOUNCE_INTENSITY = 0.34;
/** Direction the bounce comes FROM: opposite the sun in x/z, below the horizon. */
const BOUNCE_POSITION = { x: -9, y: -5, z: -7 } as const;
/**
 * Warm key sun.
 *
 * 1.8 -> 2.4 with the 2026-08-01 "everything casts shadows" pass, alongside
 * ENV 0.6 -> 0.42 and HEMI 0.35 -> 0.26. This is a REBALANCE, not a brightening:
 * the total light on a road-facing surface is about the same, but a larger share
 * of it now comes from the one source that can be OCCLUDED. At the old split the
 * shadows were geometrically correct and visually worthless — a car's shadow
 * darkened the asphalt by ~5% on screen, which under ACES read as nothing, and
 * the playtest note ("all objects other than the player don't cast shadow") was
 * partly this. At the shipped split the same shadow reads as a clear soft
 * contact patch while the world keeps its bright toy-plastic key/fill look.
 */
const SUN_INTENSITY = 2.4;
/** Cool hemisphere fill — a top-up over the environment, not the main ambient. */
const HEMI_INTENSITY = 0.17;

/** Total corridor width (lanes + both shoulders). */
const CORRIDOR_WIDTH = R.laneCount * R.laneWidth + 2 * R.shoulderWidth;
/** Half corridor width — chunks span ±this in x. */
const CORRIDOR_HALF = CORRIDOR_WIDTH / 2;

/**
 * x of the dashed separator between lane `i-1` and lane `i` (i = 1..laneCount-1).
 *
 * The lanes are centred on x = 0, so lane centres are `(i - (laneCount-1)/2) *
 * laneWidth` (sim/vehicles.ts `laneCenterX`) and the BOUNDARIES sit half a lane
 * off those: `(i - laneCount/2) * laneWidth`. For the shipped 3 × 2.7 m road
 * that is ±1.35 m — the lane EDGES.
 *
 * Bug this replaces (2026-07-31): the loop used
 * `-((laneCount-1)/2)*laneWidth + lane*laneWidth`, i.e. `laneCenterX(lane)`,
 * which painted the dashes down the middle of lanes 1 and 2 (x = 0 and +2.7).
 * On screen the road read as three wildly unequal lanes and a vehicle sitting
 * at a lane centre straddled a dashed line.
 */
export function laneBoundaryX(i: number): number {
  return (i - R.laneCount / 2) * R.laneWidth;
}

/**
 * x of the solid edge line: the outer edge of the outermost lane. The
 * `shoulderWidth` strip of asphalt between this and the guardrail at
 * `corridorHalfWidth` is the shoulder. Equals `laneCount*laneWidth/2` = 4.05 m
 * for the shipped road — i.e. `laneBoundaryX(laneCount)`.
 */
const EDGE_LINE_X = CORRIDOR_HALF - R.shoulderWidth;
// Sub-segmentation: each 50 m chunk is rebuilt as N sub-segments so the road
// can bend without 50 m rigid planes showing seams on curves. Each sub-segment
// is positioned + yawed per frame at its own curve offset/heading.
//
// 5 m (was 10 m): the 2026-07-31 curvature raise took the road's max curvature
// to 0.00154 rad/m, so consecutive 10 m segments differed by ~0.9° of heading
// and, pivoting about their own centres, opened a ~7.7 cm notch at each join
// on the tightest bends. Halving the segment length halves the notch (~3.8 cm,
// below a lane-marking width at any distance the player can see it).
const SEG_LENGTH = 5;
const SEG_COUNT = R.chunkLength / SEG_LENGTH;
/**
 * Longitudinal OVERLAP (m) baked into the ribbon geometry (asphalt, edge
 * lines, guardrails): each piece is drawn `SEG_LENGTH + SEG_OVERLAP` long but
 * still placed every `SEG_LENGTH`, so neighbours overlap instead of butting.
 *
 * Why: consecutive sub-segments are yawed by up to SEG_LENGTH·κ_max ≈ 0.44° on
 * the tightest bends and pivot about their own centres, which opens a hairline
 * notch at every join. The old tiled asphalt texture hid it; flat grey does
 * not — it read as thin light seams marching up the road. The overlap region
 * is two coplanar faces of the SAME flat colour, so the z-fight is invisible.
 */
const SEG_OVERLAP = 0.15;
const SEG_DRAW_LENGTH = SEG_LENGTH + SEG_OVERLAP;
/**
 * Longitudinal period of the dashed lane separators (m), held CONSTANT across
 * the sub-segment resize: one 3 m dash every 10 m, exactly the shipped rhythm.
 * With SEG_LENGTH = 5 that is one dash on every other sub-segment, centred in
 * it (so a dash never straddles a sub-segment join and can never poke off the
 * ribbon on a bend).
 */
const DASH_PERIOD = 10;
const SEGS_PER_DASH = DASH_PERIOD / SEG_LENGTH;



// --- Guardrail profile (playtest 2026-08-01: "the barrier is a box") -------
//
// The rail used to be literally a 0.3 × 0.9 × 5 m BoxGeometry, which is the one
// thing DESIGN_SYSTEM's geometry rules forbid: no silhouette, nothing to read.
// It is now a proper highway W-BEAM — a corrugated cross-section extruded along
// the sub-segment, with a chamfered top cap and one square post per sub-segment
// (i.e. every SEG_LENGTH = 5 m).
//
// The post is MERGED into the same geometry rather than instanced separately:
// each sub-segment already draws one rail mesh per side, and folding the post in
// keeps that at exactly one draw call instead of doubling it. Cost is 12 quads
// (beam) + 6 (post) = 36 triangles per rail segment, up from 12.
//
// The profile is SYMMETRIC in x (corrugation on both faces) so a single shared
// geometry serves both sides without a mirrored variant — only the inner face is
// ever visible from the road anyway.
/** Half-depth of a beam ridge / of a beam valley (m). */
const RAIL_RIDGE_X = 0.13;
const RAIL_VALLEY_X = 0.055;
/** Post half-extents (m). */
const RAIL_POST_HX = 0.07;
const RAIL_POST_HZ = 0.08;

/**
 * The W-beam cross-section, listed COUNTER-CLOCKWISE in the XY plane (x =
 * depth, y = height as a fraction of the rail height). The extruder below
 * relies on that winding for outward face normals.
 */
const RAIL_PROFILE: readonly (readonly [number, number])[] = [
  [RAIL_VALLEY_X, 0.47],
  [RAIL_RIDGE_X, 0.585],
  [RAIL_VALLEY_X, 0.7],
  [RAIL_RIDGE_X, 0.815],
  [0.075, 0.945],
  [0.045, 1.0],
  [-0.045, 1.0],
  [-0.075, 0.945],
  [-RAIL_RIDGE_X, 0.815],
  [-RAIL_VALLEY_X, 0.7],
  [-RAIL_RIDGE_X, 0.585],
  [-RAIL_VALLEY_X, 0.47],
];

/**
 * Build one sub-segment of guardrail: the W-beam extruded over `length` plus a
 * post at its middle. Authored with its BASE AT y = 0 (the old box was authored
 * about its centre — the call site's y placement changed with it).
 *
 * Non-indexed on purpose: every quad gets its own vertices so
 * `computeVertexNormals` produces HARD FACETS, which is the DESIGN_SYSTEM's
 * "bevel-or-nothing" reading of a corrugated beam. 36 triangles.
 */
function buildGuardrailGeometry(height: number, length: number): BufferGeometry {
  const pos: number[] = [];
  const hz = length / 2;
  /** One quad as two triangles, wound a->b->c, a->c->d. */
  const quad = (
    a: readonly [number, number, number],
    b: readonly [number, number, number],
    c: readonly [number, number, number],
    d: readonly [number, number, number],
  ): void => {
    pos.push(...a, ...b, ...c, ...a, ...c, ...d);
  };

  // Beam: extrude the profile loop along z. No end caps — consecutive segments
  // overlap by SEG_OVERLAP, so the ends are never seen.
  for (let i = 0; i < RAIL_PROFILE.length; i++) {
    const [x0, y0] = RAIL_PROFILE[i];
    const [x1, y1] = RAIL_PROFILE[(i + 1) % RAIL_PROFILE.length];
    quad(
      [x0, y0 * height, -hz],
      [x1, y1 * height, -hz],
      [x1, y1 * height, hz],
      [x0, y0 * height, hz],
    );
  }

  // Post: a square section from the ground up behind the beam's lower edge.
  const py = RAIL_PROFILE[0][1] * height + 0.04;
  const px = RAIL_POST_HX;
  const pz = RAIL_POST_HZ;
  // Four sides + top (the base is buried in the ground plane).
  quad([px, 0, -pz], [px, 0, pz], [px, py, pz], [px, py, -pz]); // +x
  quad([-px, 0, pz], [-px, 0, -pz], [-px, py, -pz], [-px, py, pz]); // -x
  quad([-px, 0, -pz], [px, 0, -pz], [px, py, -pz], [-px, py, -pz]); // -z
  quad([px, 0, pz], [-px, 0, pz], [-px, py, pz], [px, py, pz]); // +z
  quad([-px, py, -pz], [px, py, -pz], [px, py, pz], [-px, py, pz]); // top

  const g = new BufferGeometry();
  g.setAttribute("position", new Float32BufferAttribute(pos, 3));
  g.computeVertexNormals();
  return g;
}

/**
 * A recycled road chunk, built as `SEG_COUNT` sub-segment groups along its
 * length. The chunk group is repositioned along Z by `updateWorld`; each sub-
 * segment is then shifted in X and yawed per frame to follow the curve at its
 * own world z. Sub-segment local z is baked at build; only x/yaw change live.
 */
interface RoadChunk {
  group: Group;
  /**
   * Road-distance anchor (m of travel): the chunk's near edge renders at
   * z = distance - anchorD, so the chunk streams toward +Z as distance grows.
   */
  anchorD: number;
  /** Sub-segment groups; each carries its baked local z-mid for curve math. */
  segs: { g: Group; localZMid: number }[];
}

/**
 * The road world: road group + sky dome + lights. Textures load via `load()`.
 */
export class World {
  readonly group: Group;
  /** Total chunk count (fixed after warmup; no later allocation). */
  private readonly chunkCount: number;
  private readonly chunks: RoadChunk[] = [];
  private readonly chunkLength: number;

  // Shared disposables (owned here).
  private readonly sharedGeo: BufferGeometry[] = [];
  private readonly sharedMat: Material[] = [];
  private readonly hemi: HemisphereLight;
  private readonly sun: DirectionalLight;
  /** Shadowless ground-bounce fill — see BOUNCE_INTENSITY. */
  private readonly bounce: DirectionalLight;
  /** Sky texture (applied to the sky dome after load). */
  private skyTex: Texture | null = null;
  /**
   * The scene the environment was installed on. Kept ONLY so `setLightProfile`
   * can dim `environmentIntensity` per biome — the environment node itself is
   * re-tinted through `setEnvTint`, which needs no scene.
   */
  private envScene: Scene | null = null;
  /** The analytic gradient environment (see render/envGradient.ts). */
  private env: GradientEnvironment | null = null;
  /** Sky background: a Color fallback until the sky texture loads. */
  readonly skyColor: Color;

  constructor() {
    this.group = new Group();
    this.chunkLength = R.chunkLength;
    this.chunkCount = R.chunksAhead + R.chunksBehind;
    // --- Sky fallback color (used until sky.png loads) -----------------
    this.skyColor = new Color(0x87a8c8); // soft blue-grey sky

    // Toy-plastic light rig, part 3 of 4: a LOW hemisphere fill. Parts 1 and 2
    // (the gradient environment and the warm key sun) live in `load()` and just
    // below, part 4 (the ground bounce) right after this. The cel build ran the
    // hemisphere at 0.9 because it was the ONLY ambient in the scene; the
    // environment now carries the wraparound, so this is dialled back (0.26 ->
    // 0.17 when the bounce was added) to a tint on top that merely stops the
    // undersides going black.
    this.hemi = new HemisphereLight(0xbfd9ff, 0xcfc0a8, HEMI_INTENSITY);
    this.group.add(this.hemi);

    // Sun behind-right of player: +x, +y, +z quadrant. Player faces -Z, so
    // "behind" = +z; "right" = +x. Shadow casts forward-left (-x, -z).
    //
    // OWNERSHIP: the sun is CREATED here but its final placement, shadow
    // frustum and caster flags belong to render/riderCaster.ts, which
    // re-parents it (with its target) onto the camera rig root so the tight
    // shadow frustum tracks the rider. Everything below is the pre-caster
    // fallback used when no cyclist GLB is wired: a plain key light, no
    // shadows. Read `sunLight` to reach it.
    // Intensity: SUN_INTENSITY (2.4 since the shadow pass — see that constant
    // for why the key/ambient split moved). The key shapes forms AND throws
    // every shadow in the scene, so it has to carry more of the total than the
    // shadowless v1.1 rig gave it.
    this.sun = new DirectionalLight(0xfff4e0, SUN_INTENSITY);
    this.sun.position.set(8, 14, 6);
    this.sun.castShadow = false;
    this.group.add(this.sun);
    this.sun.target.position.set(0, 0, -8);
    this.group.add(this.sun.target);

    // Toy-plastic light rig, part 4: the shadowless ground-bounce fill. Its
    // colour is retargeted per biome from the hemisphere's GROUND tint (see
    // setLightTint) — light bouncing off wheat is warm, off pine needles cool.
    this.bounce = new DirectionalLight(0xcfc0a8, BOUNCE_INTENSITY);
    this.bounce.position.set(BOUNCE_POSITION.x, BOUNCE_POSITION.y, BOUNCE_POSITION.z);
    this.bounce.castShadow = false;
    this.group.add(this.bounce);
    this.bounce.target.position.set(0, 0, -8);
    this.group.add(this.bounce.target);

    // --- Build shared geometry/materials once --------------------------
    this.buildSharedAssets();

    // --- Build all chunks up front (warmup allocation) -----------------
    for (let i = 0; i < this.chunkCount; i++) {
      const chunk = this.buildChunk();
      chunk.anchorD = initialAnchor(i, this.chunkLength, R.chunksBehind);
      chunk.group.position.set(0, 0, -chunk.anchorD); // distance 0

      this.group.add(chunk.group);
      this.chunks.push(chunk);
    }
  }

  /**
   * Async-load the sky texture. Call before the loop starts. The sky texture
   * is applied to the sky dome + exposed for the caller to assign to
   * scene.background. Non-fatal on failure (keeps the colour fallback).
   *
   * There is no asphalt texture any more: the road is a flat ASPHALT_COLOR
   * matte material, authored in `buildSharedAssets`.
   *
   * When BOTH `renderer` and `scene` are supplied this also installs the
   * toy-plastic image-based lighting environment on `scene.environmentNode` at
   * ENV_INTENSITY — an analytic sky/ground gradient, not a baked cubemap. See
   * `buildEnvironment` and render/envGradient.ts.
   */
  async load(renderer?: WebGPURenderer, scene?: Scene): Promise<void> {
    if (renderer && scene) this.buildEnvironment(renderer, scene);
    const loader = new TextureLoader();
    try {
      this.skyTex = await this.loadTexture(loader, "/assets/textures/sky.png");
      const t = this.skyTex;
      t.colorSpace = SRGBColorSpace;
      // Map only the pano's sky two-thirds (skip its bottom ground band) across
      // the full dome height, so the horizon gradient lands near eye level.
      t.repeat.set(1, 0.66);
      t.offset.set(0, 0.34);
      t.needsUpdate = true;
    } catch (err) {
      console.warn("Sky texture failed to load (non-fatal):", err);
    }

  }

  /**
   * Hang the analytic gradient environment on the scene (`scene.environmentNode`).
   *
   * No PMREM, no render target, nothing to dispose — see render/envGradient.ts
   * for why the environment is a NODE now and not a baked cubemap. The
   * `renderer` argument is kept so the call site (and the `load()` contract)
   * stays the same as when this baked a PMREM.
   */
  private buildEnvironment(_renderer: WebGPURenderer, scene: Scene): void {
    try {
      this.env = createGradientEnvironment(
        new Color(ENV_SKY_DEFAULT),
        new Color(ENV_GROUND_DEFAULT),
      );
      (scene as unknown as { environmentNode: unknown }).environmentNode = this.env.node;
      scene.environment = null;
      scene.environmentIntensity = ENV_INTENSITY;
      this.envScene = scene;
    } catch (err) {
      // Non-fatal: without the environment the world still renders off the
      // sun + hemisphere, just flatter and without the clearcoat wraparound.
      console.warn("Gradient environment failed (non-fatal):", err);
      this.env = null;
      scene.environment = null;
    }
  }

  /** Expose the sun so callers can attach shadow settings later. */
  get sunLight(): DirectionalLight {
    return this.sun;
  }

  /**
   * Blend the hemisphere + directional light colours toward the given tints.
   * Called by the biome system each frame during transitions. The lights keep
   * their original intensity; only colour is retargeted.
   */
  setLightTint(hemiSky: Color, hemiGround: Color, sun: Color): void {
    this.hemi.color.copy(hemiSky);
    this.hemi.groundColor.copy(hemiGround);
    this.sun.color.copy(sun);
    // The bounce IS the ground: it carries the hemisphere's ground tint, so a
    // wheat biome bounces warm and a pine biome bounces green-cool with no
    // extra table column.
    this.bounce.color.copy(hemiGround);
  }

  /**
   * PER-BIOME LIGHT PROFILE (playtest 2026-08-02: "change the lighting in the
   * tunnel to match a tunnel's lighting").
   *
   * Scales the three light families of the rig away from their shipped outdoor
   * values. `setLightTint` moves the light's COLOUR; this moves its AMOUNT, and
   * the biome system calls both from the same per-frame blend, so a profile
   * crossfades across a seam exactly like fog and haze do.
   *
   * Every outdoor biome passes 1/1/1, so this is a no-op outside the tunnel by
   * construction — the outdoor look is byte-identical to the pre-profile build.
   *
   * @param sunMul   scales SUN_INTENSITY. The only occludable light: dropping it
   *                 is what kills a noon-sharp shadow under a concrete roof.
   * @param hemiMul  scales HEMI_INTENSITY. The tunnel raises this (and tints it
   *                 sodium through `setLightTint`) so the concrete reads as
   *                 ARTIFICIALLY lit rather than as a road with the sun off.
   * @param envMul   scales `scene.environmentIntensity` AND the ground-bounce
   *                 fill. Both are the "ambient wraparound" family — the bounce
   *                 is a directional stand-in for the same light the gradient
   *                 environment supplies — so one dial moves them together and
   *                 a dim tube does not keep an outdoor sky's worth of GI.
   */
  setLightProfile(sunMul: number, hemiMul: number, envMul: number): void {
    this.sun.intensity = SUN_INTENSITY * sunMul;
    this.hemi.intensity = HEMI_INTENSITY * hemiMul;
    this.bounce.intensity = BOUNCE_INTENSITY * envMul;
    if (this.envScene) this.envScene.environmentIntensity = ENV_INTENSITY * envMul;
  }

  /**
   * Re-tint the gradient ENVIRONMENT (the cheap GI). Called by the biome system
   * each frame with its transition-blended sky/ground colours; a no-op before
   * `load()` has built the environment. Two uniform writes — see
   * render/envGradient.ts.
   */
  setEnvTint(sky: Color, ground: Color): void {
    this.env?.setColors(sky, ground);
  }

  /**
   * ROAD STYLE hook (biome overhaul, 2026-08-01).
   *
   * Re-tint the SHARED asphalt + guardrail material instances. The biome system
   * calls this every frame with the transition-blended colours, so the road
   * restyles per biome (a subtle asphalt tint; a real colour swap on the rails,
   * e.g. the bridge's red parapet) without cloning a material per chunk and
   * without touching road GEOMETRY, lane markings or lane layout — those are
   * gameplay readability and never change (DESIGN_SYSTEM).
   *
   * The lane dashes and shoulder lines are MeshBasic and deliberately excluded:
   * markings stay crisp white in every biome.
   */
  setRoadTint(asphalt: Color, rail: Color): void {
    (this.mat.asphaltMat as Material & { color: Color }).color.copy(asphalt);
    (this.mat.railMat as Material & { color: Color }).color.copy(rail);
  }

  /** Base (untinted) road colours — the DESIGN_SYSTEM defaults. */
  static readonly BASE_ASPHALT = ASPHALT_COLOR;
  static readonly BASE_RAIL = GUARDRAIL_COLOR;

  /**
   * Attach a biome update hook. When set, `updateWorld` calls
   * `biomesHook.update(distance)` after scrolling the road, so the biome
   * system stays in lockstep with the road recycling. Owned by the biome
   * system (src/render/biomes.ts); main.ts wires it via this setter.
   */
  biomesHook: { update(distance: number): void } | null = null;

  /**
   * Scroll the world for the current (interpolated) player distance. Recycles
   * chunks that fall behind the player to the front, then bends each sub-
   * segment onto the curve at its own world z — lateral offset via curveOffset
   * and absolute heading via curveHeading so the ribbon aligns with the
   * centerline. No allocation after the constructor.
   *
   * @param distance player.distance (m travelled). Near-edge z = distance - anchorD.
   */
  updateWorld(distance: number): void {
    // Glue the procedural asphalt detail to the road: the shader's longitudinal
    // coordinate is `distance - positionWorld.z`, so it needs the SAME
    // interpolated distance the chunks are positioned with (plastic.ts
    // `asphaltMaterial`, ROAD SPACE).
    this.mat.asphaltMat.setRoadDistance(distance);
    for (let i = 0; i < this.chunks.length; i++) {
      const c = this.chunks[i];
      // Ring-shift the anchor if this chunk has passed behind the camera; the
      // chunk then reappears at the far end of the ahead window.
      c.anchorD = recycleAnchor(
        c.anchorD,
        distance,
        this.chunkLength,
        this.chunkCount,
        R.chunksBehind,
      );
      const z = distance - c.anchorD;
      c.group.position.set(0, 0, z);

      // Bend each sub-segment onto the curve. The sub-segment's world z mid =
      // chunk z + baked local z mid; r = D - worldZMid. The segment pivots
      // about its own center (group at localZMid) so x/yaw don't skew it.
      const segs = c.segs;
      for (let s = 0; s < segs.length; s++) {
        const seg = segs[s];
        const worldZMid = z + seg.localZMid;
        const rMid = distance - worldZMid;
        seg.g.position.x = curveOffset(worldZMid, distance);
        // Absolute curve heading: align the ribbon with the centerline tangent
        // at its own r (the camera yaws separately at D, so the bend reads).
        seg.g.rotation.y = -Math.atan(curveTangent(rMid));
      }
    }

    // The sun is NOT repositioned here: riderCaster.ts parents it (and its
    // target) to the camera rig root, whose x is the interpolated player x, so
    // the shadow frustum tracks the rider without a second copy of that math.

    // Keep the sky dome centered on the player (it never scrolls — only the
    // road does; the sky is infinitely far).
    // Stage 5 biome system hook: scrolls roadside content + blends fog/light
    // in lockstep with the road recycling. No-op until main.ts attaches it.
    if (this.biomesHook) this.biomesHook.update(distance);
  }

  /** Release GPU resources. */
  dispose(): void {
    for (const c of this.chunks) {
      c.group.traverse((o: unknown) => {
        const m = o as Mesh;
        if (m.geometry && !this.sharedGeo.includes(m.geometry)) {
          m.geometry.dispose();
        }
      });
      this.group.remove(c.group);
    }
    for (const g of this.sharedGeo) g.dispose();
    for (const m of this.sharedMat) m.dispose();
    if (this.skyTex) this.skyTex.dispose();
    // The gradient environment owns no GPU resources (see envGradient.ts).
    this.env = null;
    this.chunks.length = 0;
  }

  // -----------------------------------------------------------------------
  // Internals
  // -----------------------------------------------------------------------

  private loadTexture(loader: TextureLoader, path: string): Promise<Texture> {
    return new Promise<Texture>((resolve, reject) => {
      loader.load(path, resolve, undefined, reject);
    });
  }

  private buildSharedAssets(): void {
    // Asphalt plane — one per sub-segment. Reused by every seg. LIT (not
    // basic) because the road must RECEIVE the rider's shadow, and lightly
    // clear-coated (`asphalt` params) so the largest surface in the frame
    // shares the world's toy-plastic response instead of sitting flat and dead.
    // ONE material instance for every chunk — never build these per chunk.
    // BIOME HOOK: `setRoadTint` re-colours THIS instance (and the rail one) at
    // biome transitions, so the whole road restyles with a single colour write
    // and zero extra materials/draw calls.
    const asphaltGeo = new PlaneGeometry(CORRIDOR_WIDTH, SEG_DRAW_LENGTH);
    asphaltGeo.rotateX(-Math.PI / 2); // lay flat on XZ, normal +Y
    this.sharedGeo.push(asphaltGeo);
    // The asphalt carries a procedural TSL detail node (mottle + near-camera
    // speckle + wheel tracks) over the tinted base — see plastic.ts
    // `asphaltMaterial`. `setRoadTint` still just writes `material.color`;
    // the node multiplies whatever that is.
    const asphaltMat = asphaltMaterial({
      color: ASPHALT_COLOR,
      laneWidth: R.laneWidth,
      laneHalfWidth: EDGE_LINE_X,
      name: "asphalt",
    });
    this.sharedMat.push(asphaltMat);

    // Lane dash strip (a single dash unit; one per DASH_PERIOD). Unlit basic
    // white so the markings stay crisp at any sun angle / biome tint.
    const dashGeo = new PlaneGeometry(0.18, 3.0);
    dashGeo.rotateX(-Math.PI / 2);
    this.sharedGeo.push(dashGeo);
    // The markings ride the curved-earth bend with the asphalt they sit on
    // (render/earthCurve.ts) — a flat dash on a bent road would sink through it.
    const laneMat = applyEarthCurve(new MeshBasicMaterial({ color: LANE_MARK_COLOR }));
    this.sharedMat.push(laneMat);

    // Shoulder edge line (solid, runs one sub-segment length). Unlit, as above.
    const edgeGeo = new PlaneGeometry(0.2, SEG_DRAW_LENGTH);
    edgeGeo.rotateX(-Math.PI / 2);
    this.sharedGeo.push(edgeGeo);
    const edgeMat = applyEarthCurve(new MeshBasicMaterial({ color: SHOULDER_LINE_COLOR }));
    this.sharedMat.push(edgeMat);

    // Guardrail: a W-beam profile + a post, one per sub-segment per side.
    const railGeo = buildGuardrailGeometry(R.guardrailHeight, SEG_DRAW_LENGTH);
    this.sharedGeo.push(railGeo);
    // Orange rails are CANDY plastic — glossy vinyl toy, the same surface class
    // as the vehicle bodies, so the roadside furniture belongs to the same set
    // as the traffic. One shared instance across every chunk.
    const railMat = plasticMaterial({
      color: GUARDRAIL_COLOR,
      name: "guardrail",
      kind: "candy",
    });
    this.sharedMat.push(railMat);

    // Stash references by index for buildChunk.
    this.geo = { asphaltGeo, dashGeo, edgeGeo, railGeo };
    this.mat = { asphaltMat, laneMat, edgeMat, railMat };
  }

  private geo!: {
    asphaltGeo: BufferGeometry;
    dashGeo: BufferGeometry;
    edgeGeo: BufferGeometry;
    railGeo: BufferGeometry;
  };
  private mat!: {
    asphaltMat: AsphaltMaterial;
    laneMat: Material;
    edgeMat: Material;
    railMat: Material;
  };

  private buildChunk(): RoadChunk {
    const group = new Group();
    const segs: { g: Group; localZMid: number }[] = [];

    // Build SEG_COUNT sub-segments. Sub i spans chunk-local z
    // [-(i+1)*SEG_LENGTH, -i*SEG_LENGTH]; its group sits at the mid so the
    // per-frame curve x/yaw pivots about the segment center (no lateral skew).
    for (let i = 0; i < SEG_COUNT; i++) {
      const sg = new Group();
      // Chunk-local mid z of this sub-segment (negative = ahead).
      const localZMid = -(i + 0.5) * SEG_LENGTH;
      sg.position.set(0, 0, localZMid);

      // Asphalt: centered at the sub-segment origin (group mid), flat.
      const asphalt = new Mesh(this.geo.asphaltGeo, this.mat.asphaltMat);
      asphalt.position.set(0, 0, 0);
      asphalt.receiveShadow = true; // road receives the rider shadow
      sg.add(asphalt);

      // Lane dashes: one 3 m dash every DASH_PERIOD metres, centred in the
      // sub-segment that owns it (sub-local z = 0 is the segment mid).
      if (i % SEGS_PER_DASH === 0) {
        for (let lane = 1; lane < R.laneCount; lane++) {
          const x = laneBoundaryX(lane);
          const dash = new Mesh(this.geo.dashGeo, this.mat.laneMat);
          dash.name = "laneDash";
          dash.position.set(x, 0.02, 0);
          sg.add(dash);
        }
      }

      // Shoulder edge lines (solid) at the inner edge of each shoulder.
      const edgeL = new Mesh(this.geo.edgeGeo, this.mat.edgeMat);
      edgeL.name = "edgeLine";
      edgeL.position.set(-EDGE_LINE_X, 0.02, 0);
      sg.add(edgeL);
      const edgeR = new Mesh(this.geo.edgeGeo, this.mat.edgeMat);
      edgeR.name = "edgeLine";
      edgeR.position.set(EDGE_LINE_X, 0.02, 0);
      sg.add(edgeR);

      // Guardrails: W-beam + post on both sides at the corridor edge. The
      // profile is authored base-at-zero (the old box was centre-at-zero), so
      // these sit at y = 0 and the post reaches the ground. They CAST
      // (2026-08-01): a 0.9 m rail throws a ~1.5 m band onto the shoulder at
      // this sun elevation, which is what stops the corridor edge from reading
      // as a decal painted on the asphalt.
      const railL = new Mesh(this.geo.railGeo, this.mat.railMat);
      railL.position.set(-CORRIDOR_HALF - 0.15, 0, 0);
      railL.castShadow = true;
      railL.receiveShadow = true;
      sg.add(railL);
      const railR = new Mesh(this.geo.railGeo, this.mat.railMat);
      railR.position.set(CORRIDOR_HALF + 0.15, 0, 0);
      railR.castShadow = true;
      railR.receiveShadow = true;
      sg.add(railR);

      group.add(sg);
      segs.push({ g: sg, localZMid });
    }

    return { group, anchorD: 0, segs };
  }
}
